import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "wouter";
import {
  CheckCircle2,
  Lock,
  AlertTriangle,
  CreditCard,
  ShieldCheck,
  Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SunLogo } from "@/components/SunLogo";
import { formatDateTime, formatMoney } from "@/lib/format";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  AddressElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

interface SignerView {
  id: string;
  contractTitle: string;
  signerName: string;
  signerEmail: string;
  totalValueCents: number;
  depositAmountCents: number;
  signedAt: string | null;
  status: string;
  paidAt?: string | null;
  paidAmountCents?: number | null;
  paidChoice?: "deposit" | "full" | null;
  refundedAmountCents?: number | null;
  lastRefundAt?: string | null;
  lastRefundId?: string | null;
}

const PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;

let stripePromise: Promise<Stripe | null> | null = null;
function getStripe() {
  if (!stripePromise) {
    stripePromise = PUBLISHABLE_KEY ? loadStripe(PUBLISHABLE_KEY) : Promise.resolve(null);
  }
  return stripePromise;
}

export default function SignRepay() {
  const { token } = useParams<{ token: string }>();
  const { data } = useQuery<SignerView>({
    queryKey: [`/api/contracts/by-token/${token}`],
    staleTime: 0,
    refetchOnMount: "always",
  });

  // Refund-aware: a refund reduces effective payment, so the customer may owe again.
  const netPaid = data
    ? Math.max(0, (data.paidAmountCents || 0) - (data.refundedAmountCents || 0))
    : 0;
  const total = data?.totalValueCents || 0;
  const balance = data ? Math.max(0, total - netPaid) : 0;
  // 5% tolerance — see deriveDisplayStatus. Anything within ±5% of the
  // contract total counts as paid in full so the re-pay flow doesn't ask
  // the customer for a tiny rounding-driven balance.
  const isPaidInFull = !!data && (total > 0 ? netPaid >= total * 0.95 : netPaid > 0);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="border-b border-border bg-card/60 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="text-primary"><SunLogo size={26} /></span>
            <div className="leading-tight">
              <div className="text-[15px] font-semibold tracking-tight">Sun Rental</div>
              <div className="text-[11px] text-muted-foreground -mt-0.5">
                {isPaidInFull ? "Balance paid" : "Pay remaining balance"}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-2xl w-full mx-auto px-5 sm:px-6 py-10 md:py-16">
        {!data ? (
          <div className="text-center text-sm text-muted-foreground">Loading…</div>
        ) : isPaidInFull ? (
          <PaidInFull data={data} />
        ) : (
          <RepayStep data={data} token={token!} balance={balance} />
        )}
      </div>

      <footer className="border-t border-border bg-card/40">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-5 text-[11px] text-muted-foreground text-center">
          <div>
            <span className="font-medium text-foreground">Sun Rental Center</span> · 7467 Mentor Ave, Mentor OH 44060
          </div>
          <div className="mt-0.5">
            440-942-6106 ·{" "}
            <a className="hover:underline" href="mailto:events@sunrental.com">
              events@sunrental.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function PaidInFull({ data }: { data: SignerView }) {
  return (
    <div className="text-center">
      <div className="h-16 w-16 rounded-full bg-[hsl(var(--status-signed))]/12 text-[hsl(var(--status-signed))] mx-auto flex items-center justify-center mb-5">
        <CheckCircle2 className="h-8 w-8" />
      </div>
      <h1 className="text-3xl font-semibold tracking-tight" data-testid="text-paid-headline">
        Paid in full
      </h1>
      <p className="text-base text-muted-foreground mt-3 max-w-md mx-auto">
        Thanks{data.signerName ? `, ${data.signerName.split(" ")[0]}` : ""}. Your balance for{" "}
        <span className="font-medium text-foreground">{data.contractTitle}</span> has been paid.
      </p>

      <div className="mt-8 rounded-xl border border-card-border bg-card p-5 md:p-6 text-left">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Receipt</div>
        <div className="text-lg font-medium mt-1">{data.contractTitle}</div>
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Total contract</div>
            <div className="text-sm font-medium tabular-nums mt-1">{formatMoney(data.totalValueCents)}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Total paid</div>
            <div className="text-sm font-medium tabular-nums mt-1 text-[hsl(var(--status-signed))]" data-testid="text-net-paid">
              {formatMoney(Math.max(0, (data.paidAmountCents ?? 0) - (data.refundedAmountCents ?? 0)))}
            </div>
            {(data.refundedAmountCents ?? 0) > 0 && (
              <div className="text-[11px] text-muted-foreground mt-0.5" data-testid="text-paid-breakdown">
                {formatMoney(data.paidAmountCents ?? 0)} paid − {formatMoney(data.refundedAmountCents ?? 0)} refunded
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-10 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
        <Lock className="h-3 w-3" /> You may now close this window.
      </div>
    </div>
  );
}

function RepayStep({
  data,
  token,
  balance,
}: {
  data: SignerView;
  token: string;
  balance: number;
}) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [intentAmount, setIntentAmount] = useState<number>(balance);
  const [retryBanner, setRetryBanner] = useState<string | null>(null);
  const [initialError, setInitialError] = useState<string | null>(null);

  const intentMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/contracts/by-token/${token}/payment-intent`, {
        choice: "balance",
      });
      return (await res.json()) as { clientSecret: string; amount: number };
    },
    onSuccess: (resp) => {
      setClientSecret(resp.clientSecret);
      setIntentAmount(resp.amount);
      setInitialError(null);
    },
    onError: (err: any) => {
      setInitialError(err?.message || "Could not start payment.");
    },
  });

  useEffect(() => {
    intentMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stripeP = useMemo(() => getStripe(), []);

  return (
    <>
      <div className="text-center">
        <div className="h-14 w-14 rounded-full bg-primary/10 text-primary mx-auto flex items-center justify-center mb-4">
          <CreditCard className="h-7 w-7" />
        </div>
        <h1 className="text-3xl font-semibold tracking-tight">Pay remaining balance</h1>
        <p className="text-base text-muted-foreground mt-3 max-w-md mx-auto">
          Hi{data.signerName ? ` ${data.signerName.split(" ")[0]}` : ""}, your card on file
          couldn't be charged automatically. Please review and submit your payment below to
          settle the balance for your reservation.
        </p>
      </div>

      <div className="mt-8 rounded-xl border border-card-border bg-card p-5 md:p-6">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Contract</div>
        <div className="text-lg font-medium mt-1">{data.contractTitle}</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Total</div>
            <div className="text-sm font-medium tabular-nums mt-1">{formatMoney(data.totalValueCents)}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Already paid</div>
            <div className="text-sm font-medium tabular-nums mt-1" data-testid="text-already-paid">
              {formatMoney(Math.max(0, (data.paidAmountCents ?? 0) - (data.refundedAmountCents ?? 0)))}
            </div>
            {(data.refundedAmountCents ?? 0) > 0 && (
              <div className="text-[11px] text-muted-foreground mt-0.5">
                after {formatMoney(data.refundedAmountCents ?? 0)} refund
              </div>
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Balance due</div>
            <div className="text-sm font-semibold tabular-nums mt-1 text-primary">
              {formatMoney(balance)}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Signed</div>
            <div className="text-sm font-medium mt-1">{formatDateTime(data.signedAt)}</div>
          </div>
        </div>
      </div>

      {retryBanner && (
        <div
          className="mt-5 rounded-md border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/8 text-[hsl(var(--destructive))] p-3 flex gap-3 text-sm"
          data-testid="banner-payment-error"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Payment didn't go through</div>
            <div className="opacity-90">{retryBanner} Please review the details and try again.</div>
          </div>
        </div>
      )}

      <div className="mt-6 rounded-xl border border-card-border bg-card p-5 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-medium">Card details</div>
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5" />
            Secured by Stripe
          </div>
        </div>

        {!PUBLISHABLE_KEY && (
          <div className="text-sm text-[hsl(var(--destructive))]">
            Stripe publishable key is missing. Add VITE_STRIPE_PUBLISHABLE_KEY to enable payments.
          </div>
        )}

        {initialError && (
          <div className="text-sm text-[hsl(var(--destructive))] mb-3">{initialError}</div>
        )}

        {clientSecret && PUBLISHABLE_KEY ? (
          <Elements
            key={clientSecret}
            stripe={stripeP}
            options={{
              clientSecret,
              appearance: { theme: "stripe" },
            }}
          >
            <PayForm
              token={token}
              amount={intentAmount}
              signerName={data.signerName}
              signerEmail={data.signerEmail}
              onFailure={(msg) => setRetryBanner(msg)}
              onSuccess={() => {
                setRetryBanner(null);
                queryClient.invalidateQueries({
                  queryKey: [`/api/contracts/by-token/${token}`],
                });
              }}
            />
          </Elements>
        ) : (
          <div className="h-32 rounded-md bg-muted/30 animate-pulse" />
        )}
      </div>

      <div className="mt-6 rounded-xl border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground flex gap-3">
        <Mail className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <span className="text-foreground font-medium">A receipt will be emailed</span> to you and
          to Sun Rental once your payment is processed.
        </div>
      </div>

      <div className="mt-6 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
        <Lock className="h-3 w-3" /> Card details are submitted directly to Stripe and never touch our servers.
      </div>
    </>
  );
}

function PayForm({
  token,
  amount,
  signerName,
  signerEmail,
  onSuccess,
  onFailure,
}: {
  token: string;
  amount: number;
  signerName: string;
  signerEmail: string;
  onSuccess: () => void;
  onFailure: (msg: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setInlineError(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: {
        payment_method_data: {
          billing_details: {
            name: signerName,
            email: signerEmail,
          },
        },
      },
    });

    if (error) {
      const msg = error.message || "Card was declined.";
      setInlineError(msg);
      onFailure(msg);
      setSubmitting(false);
      return;
    }

    if (paymentIntent && paymentIntent.status === "succeeded") {
      try {
        await apiRequest("POST", `/api/contracts/by-token/${token}/payment-confirm`);
        onSuccess();
      } catch (err: any) {
        const msg = err?.message || "Payment succeeded but we couldn't finalize. Please contact us.";
        setInlineError(msg);
        onFailure(msg);
      }
    } else {
      const msg = `Payment is ${paymentIntent?.status || "not yet complete"}. Please try again.`;
      setInlineError(msg);
      onFailure(msg);
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handlePay} className="space-y-4">
      <div className="rounded-md border border-border bg-card p-4">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
          Billing address
        </div>
        <AddressElement
          options={{
            mode: "billing",
            defaultValues: {
              name: signerName,
            },
            fields: { phone: "always" },
            validation: { phone: { required: "always" } },
          }}
        />
      </div>
      <div className="rounded-md border border-border bg-card p-4">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
          Card details
        </div>
        <PaymentElement
          options={{
            layout: "tabs",
            fields: {
              billingDetails: {
                name: "never",
                email: "never",
                phone: "never",
                address: "never",
              },
            },
            wallets: { applePay: "never", googlePay: "never", link: "never" },
            terms: { card: "never" },
          }}
        />
      </div>
      {inlineError && (
        <div className="text-sm text-[hsl(var(--destructive))]" data-testid="text-pay-inline-error">
          {inlineError}
        </div>
      )}
      <Button
        type="submit"
        className="w-full h-11 text-base"
        disabled={!stripe || !elements || submitting}
        data-testid="button-pay-balance"
      >
        {submitting ? "Processing…" : `Pay ${formatMoney(amount)} (balance)`}
      </Button>
      <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
        <ShieldCheck className="h-3 w-3 inline-block mr-1 -mt-0.5" />
        Your billing address is verified against your card to protect against fraud.
      </p>
    </form>
  );
}
